import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../api/api.js";
import { useAuth } from "./AuthContext.js";
import { getSocket } from "../socket.js";
import "../styles/EmployeeAttendance.css";

const OutsourceTimeTracker = () => {
  const [currentSession, setCurrentSession] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [loading, setLoading] = useState(true); // Start with loading true
  const [error, setError] = useState(null);
  const [todaySessions, setTodaySessions] = useState([]);
  const [canStartNewSession, setCanStartNewSession] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false); // Track if initial data load is complete
  const { id: user_id, role } = useAuth();

  const timerRef = useRef(null);
  const realTimeTimerRef = useRef(null);
  const socketDebounceRef = useRef(null);

  // API Endpoints
  const API_ENDPOINTS = {
    clockIn: "/clock/clock-in",
    clockOut: "/clock/clock-out",
    getTodaySessions: `/clock/today/${user_id}`,
  };

  // Helper functions for London timezone
  const getLondonTime = (date = new Date()) => {
    return new Date(
      date.toLocaleString("en-US", { timeZone: "Europe/London" })
    );
  };

  const formatTimeWithAMPM = (date) => {
    if (!date) return "";
    const londonTime = getLondonTime(date);
    return londonTime.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatElapsed = (ms) => {
    const totalSec = Math.floor(ms / 1000);
    const hrs = String(Math.floor(totalSec / 3600)).padStart(2, "0");
    const mins = String(Math.floor((totalSec % 3600) / 60)).padStart(2, "0");
    const secs = String(totalSec % 60).padStart(2, "0");
    return `${hrs}:${mins}:${secs}`;
  };

  // Real-time timer that updates every second
  const startRealTimeTimer = () => {
    if (!realTimeTimerRef.current && currentSession && !currentSession.clock_out) {
      realTimeTimerRef.current = setInterval(() => {
        updateElapsedTime();
      }, 1000);
    }
  };

  const stopRealTimeTimer = () => {
    if (realTimeTimerRef.current) {
      clearInterval(realTimeTimerRef.current);
      realTimeTimerRef.current = null;
    }
  };

  const updateElapsedTime = () => {
    if (!currentSession || currentSession.clock_out) return;

    const now = new Date();
    const elapsed = now.getTime() - new Date(currentSession.clock_in).getTime();
    setElapsedTime(Math.max(0, elapsed));
    // Note: Timer runs continuously even across date changes (past midnight)
    // allowing outsource sessions to span multiple days if needed
  };

  // Load today's sessions
  const loadTodaySessions = async (isInitialLoad = false) => {
    try {
      setLoading(true);
      setError(null);



      const response = await axiosInstance.get(API_ENDPOINTS.getTodaySessions);

      if (response.data.success && response.data.data) {
        const sessions = Array.isArray(response.data.data) ? response.data.data : [response.data.data];

        setTodaySessions(sessions);

        // Find active session (not clocked out) - more robust check
        const activeSession = sessions.find(session => {
          const hasClockIn = session.clock_in !== null && session.clock_in !== undefined;
          const hasNoClockOut = session.clock_out === null || session.clock_out === undefined;
          return hasClockIn && hasNoClockOut;
        });

        if (activeSession) {
          setCurrentSession(activeSession);
          setElapsedTime(0); // Reset elapsed time for active session
          startRealTimeTimer();
        } else {
          setCurrentSession(null);
          setElapsedTime(0);
        }

        // Mark data as loaded on initial load
        if (isInitialLoad) {
          setDataLoaded(true);
        }
      } else {
        // No sessions found for today
        resetDailyStates();
        if (isInitialLoad) {
          setDataLoaded(true);
        }
      }
    } catch (error) {
      console.error("[OutsourceTimeTracker] Load session error:", error.response?.status);

      if (error.response?.status === 404) {
        // Backend endpoint not available - use mock data for outsource users
        resetDailyStates();
        setError(null);
        if (isInitialLoad) {
          setDataLoaded(true);
        }
      } else {
        // Handle other errors normally
        setError("Failed to load today's sessions");
        if (isInitialLoad) {
          setDataLoaded(true);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const resetDailyStates = () => {
    setCurrentSession(null);
    setElapsedTime(0);
    setTodaySessions([]);
    setCanStartNewSession(true);
  };

  const handleClockIn = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("Attempting clock in for user:", user_id);

      const response = await axiosInstance.post(API_ENDPOINTS.clockIn, {
        user_id,
        clockInTime: new Date().toISOString(),
      });

      console.log("Clock in response:", response.data);

      if (response.data.success) {
        // Reload sessions to get updated data
        await loadTodaySessions();
      } else {
        throw new Error(response.data.message || "Failed to clock in");
      }
    } catch (error) {
      console.error("Clock in error:", error);
      console.error("Error response:", error.response?.data);
      setError(
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to clock in. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    // Find active session if currentSession is not set
    const activeSession = currentSession || todaySessions.find(session => session.clock_in && !session.clock_out);

    if (!activeSession) {
      setError("No active session found to clock out from.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.post(API_ENDPOINTS.clockOut, {
        user_id,
        sessionId: activeSession.id,
        clockOutTime: new Date().toISOString(),
        totalWorkedTime: elapsedTime,
      });

      if (response.data.success) {
        const session = response.data.attendance;
        const updatedSession = {
          ...activeSession,
          clock_out: session.clock_out,
          duration: elapsedTime
        };

        setCurrentSession(null);
        setTodaySessions(prev => prev.map(sess =>
          sess.id === activeSession.id ? updatedSession : sess
        ));
        setElapsedTime(0);
        setCanStartNewSession(true);
        stopRealTimeTimer();

        // Reload sessions to get updated data
        await loadTodaySessions();
      } else {
        throw new Error(response.data.message || "Failed to clock out");
      }
    } catch (error) {
      console.error("Clock out error:", error.response?.data || error.message);
      setError(
        error.response?.data?.error || "Failed to clock out. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Load sessions on component mount
  useEffect(() => {
    loadTodaySessions(true); // Mark as initial load
  }, [user_id]);

  // Socket event listeners for real-time updates
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handleClockIn = (data) => {
      // Update session data when new clock-in detected
      if (socketDebounceRef.current) {
        clearTimeout(socketDebounceRef.current);
      }
      socketDebounceRef.current = setTimeout(() => {
        loadTodaySessions();
      }, 500); // 500ms debounce
    };

    const handleClockOut = (data) => {
      // Debounce socket events to prevent rapid firing
      if (socketDebounceRef.current) {
        clearTimeout(socketDebounceRef.current);
      }
      socketDebounceRef.current = setTimeout(() => {
        loadTodaySessions();
      }, 500); // 500ms debounce
    };

    const handleLateClockIn = (data) => {
      // Debounce socket events to prevent rapid firing
      if (socketDebounceRef.current) {
        clearTimeout(socketDebounceRef.current);
      }
      socketDebounceRef.current = setTimeout(() => {
        loadTodaySessions();
      }, 500); // 500ms debounce
    };

    // Listen for clock events
    socket.on('new_clock_in', handleClockIn);
    socket.on('new_clock_out', handleClockOut);
    socket.on('late_clock_in', handleLateClockIn);

    // Cleanup listeners on unmount
    return () => {
      socket.off('new_clock_in', handleClockIn);
      socket.off('new_clock_out', handleClockOut);
      socket.off('late_clock_in', handleLateClockIn);
      if (socketDebounceRef.current) {
        clearTimeout(socketDebounceRef.current);
      }
    };
  }, [user_id]);

  // Refresh page automatically every 1 hour
    useEffect(() => {
      const intervalId = setInterval(() => {
        window.location.reload();
      }, 3600000); // 1 hour in milliseconds
      // Cleanup interval on unmount
      return () => clearInterval(intervalId);
    }, []);

  // Update elapsed time when current session changes
  useEffect(() => {
    if (currentSession && !currentSession.clock_out) {
      updateElapsedTime();
      startRealTimeTimer();
    } else {
      stopRealTimeTimer();
    }
  }, [currentSession]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (realTimeTimerRef.current) {
        clearInterval(realTimeTimerRef.current);
      }
    };
  }, []);

  // Prevent this component from running for non-outsource users
  if (role !== 'outsource') {
    console.warn('[OutsourceTimeTracker] Component loaded for non-outsource user, skipping render');
    return null;
  }

  if (loading && todaySessions.length === 0) {
    return (
      <div className="card time-tracking">
        <div className="loading-state">Loading your sessions...</div>
      </div>
    );
  }

  return (
    <div className="card time-tracking">
      <h2 className="card-title">Outsource Time Tracker</h2>

      {error && (
        <div
          className="error-message"
          style={{
            backgroundColor: "#fee2e2",
            color: "#dc2626",
            padding: "12px",
            borderRadius: "8px",
            marginBottom: "16px",
            border: "1px solid #fecaca",
          }}
        >
          {error}
        </div>
      )}

      {/* Clock In/Out Buttons */}
      <div className="time-tracking-buttons">
        <div className="button-group-left">
          {dataLoaded && !currentSession ? (
            <button
              onClick={handleClockIn}
              disabled={loading}
              className="btn btn-blue clock-in-btn"
            >
              {loading ? "Clocking In..." : "Clock In"}
            </button>
          ) : dataLoaded && currentSession ? (
            <div className="clock-status">
              Clocked in at: {formatTimeWithAMPM(new Date(currentSession.clock_in))}
            </div>
          ) : (
            <div className="clock-status">Loading session status...</div>
          )}
        </div>

        <div className="button-group-right">
          {dataLoaded && currentSession && !currentSession.clock_out ? (
            <button
              onClick={handleClockOut}
              disabled={loading}
              className="btn btn-blue clock-out-btn"
            >
              {loading ? "Clocking Out..." : "Clock Out"}
            </button>
          ) : dataLoaded && currentSession && currentSession.clock_out ? (
            <div className="clock-status">
              Clocked out at: {formatTimeWithAMPM(new Date(currentSession.clock_out))}
            </div>
          ) : dataLoaded ? null : (
            <div className="clock-status">Loading...</div>
          )}
        </div>
      </div>

      {/* Timer Display */}
      <div className="timer-display">
        <h3>{formatElapsed(elapsedTime)}</h3>
        {currentSession && !currentSession.clock_out && (
          <div style={{ fontSize: "14px", color: "#10b981", marginTop: "8px" }}>
            Currently Working - Session {todaySessions.length}
          </div>
        )}

        {/* Current Session Progress Bar */}
        {currentSession && !currentSession.clock_out && (
          <div className="session-progress">
            <div className="progress-header">
              <span>Session Progress</span>
              <span className="progress-percentage">{Math.min((elapsedTime / (8 * 60 * 60 * 1000)) * 100, 100).toFixed(0)}%</span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${Math.min((elapsedTime / (8 * 60 * 60 * 1000)) * 100, 100)}%`,
                  background: elapsedTime >= (8 * 60 * 60 * 1000) ? '#10b981' : elapsedTime >= (4 * 60 * 60 * 1000) ? '#f59e0b' : '#3b82f6'
                }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Today's Sessions Summary */}
      {todaySessions.length > 0 && (
        <div className="sessions-summary" style={{ marginTop: "20px" }}>
          <h4>Today's Sessions</h4>
          {todaySessions.map((session, index) => (
            <div key={session.id} style={{
              padding: "8px",
              border: "1px solid #e5e7eb",
              borderRadius: "4px",
              marginBottom: "8px"
            }}>
              <div>Session {index + 1}: {formatTimeWithAMPM(new Date(session.clock_in))}
                {session.clock_out && ` - ${formatTimeWithAMPM(new Date(session.clock_out))}`}
              </div>
              {session.duration > 0 && (
                <div style={{ fontSize: "12px", color: "#6b7280" }}>
                  Duration: {formatElapsed(session.duration)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}


    </div>
  );
};

export default OutsourceTimeTracker;
